/**
 * CarriersGrid — drug × carrier-solution compatibility table.
 *
 * Carriers tab counterpart to MatrixGrid. Same sticky-cells pattern
 * (top row + leftmost column freeze for Excel-style scroll), but the
 * column axis is the small fixed set of IV carrier solutions
 * (NaCl 0.9%, Glucose 5/10/20%, NaCl 3%, Ringer's), so headers can be
 * horizontal — no rotated text and no horizontal scroll needed at
 * realistic viewports.
 *
 * Selection model is shared with the main matrix: row-header click sets
 * the drug axis, col-header click sets the carrier axis, cell click sets
 * both. The board's existing slot state ((selectedRowDrugId,
 * selectedColDrugId) and DrugSidePanel handle (drugA, drugB) as
 * "drug + carrier" without extra plumbing.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type {
  CompatibilityBoardState,
  CompatibilityCell,
  CompatibilityDrug,
  VerdictTone,
} from '@frontend/components/ysite/types'
import { getCell, VERDICT_TONE } from '@frontend/components/ysite/types'
import { TONE_FILL } from '@frontend/components/ysite/verdict.ui'
import { splitLabel } from '@frontend/components/ysite/label'

interface CarriersGridProps {
  state: CompatibilityBoardState
  /** Drug axis (rows). Caller passes filtered list — non-carrier, non-sentinel. */
  drugDrugs: CompatibilityDrug[]
  /** Carrier axis (cols). Caller passes the carrier-only list in render order. */
  carrierDrugs: CompatibilityDrug[]
  /** Tones that render at full opacity — others dim, structure preserved. */
  visibleTones: Set<VerdictTone>
  selectedDrugId: string | null
  selectedPair: [string, string] | null
  onSelectCell: (drug: CompatibilityDrug, carrier: CompatibilityDrug, cell: CompatibilityCell) => void
  onSelectDrug: (drug: CompatibilityDrug) => void
  onSelectCarrier: (carrier: CompatibilityDrug) => void
}

export function CarriersGrid({
  state,
  drugDrugs,
  carrierDrugs,
  visibleTones,
  selectedDrugId,
  selectedPair,
  onSelectCell,
  onSelectDrug,
  onSelectCarrier,
}: CarriersGridProps) {
  const { t } = useTranslation()

  const activeIds = new Set<string>()
  if (selectedDrugId) activeIds.add(selectedDrugId)
  if (selectedPair) {
    activeIds.add(selectedPair[0])
    activeIds.add(selectedPair[1])
  }

  if (drugDrugs.length === 0 || carrierDrugs.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-[15px] text-neutral-500">
        {t('compatibility.carriers.empty', 'No carrier compatibility data in this matrix.')}
      </div>
    )
  }

  return (
    <div className="relative min-h-0 min-w-0 flex-1">
      <div
        className="compat-matrix-scroll absolute inset-0 overflow-auto rounded-[18px] border border-[var(--hairline)] bg-[var(--canvas)]"
        tabIndex={0}
        role="region"
        aria-label={t('compatibility.carriers.title', 'Carrier compatibility')}
      >
        <table className="w-full border-separate border-spacing-[3px]">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 bg-[var(--canvas)]" />
              {carrierDrugs.map((c) => {
                const active = activeIds.has(c.id)
                return (
                  <th
                    key={c.id}
                    scope="col"
                    className="sticky top-0 z-20 bg-[var(--canvas)] p-0 align-bottom"
                  >
                    <button
                      type="button"
                      onClick={() => onSelectCarrier(c)}
                      title={c.label}
                      className={cn(
                        'block whitespace-nowrap px-3 pb-1.5 pt-2 text-center text-[12px] font-semibold leading-tight transition active:scale-95',
                        active
                          ? 'text-primary-600'
                          : 'text-neutral-700 hover:text-primary-600',
                      )}
                    >
                      {c.label}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {drugDrugs.map((d) => {
              const drugActive = activeIds.has(d.id)
              const { name, concentration } = splitLabel(d.label)
              return (
                <tr key={d.id}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-[var(--canvas)] p-0 text-right"
                  >
                    <button
                      type="button"
                      onClick={() => onSelectDrug(d)}
                      title={d.label}
                      className={cn(
                        'block max-w-[260px] truncate pr-2.5 text-right text-[13px] leading-tight',
                        'transition active:scale-95',
                        drugActive
                          ? 'font-semibold text-primary-600'
                          : 'text-neutral-700 hover:text-primary-600',
                      )}
                    >
                      <span className="block truncate font-semibold">{name || d.label}</span>
                      {concentration && (
                        <span className="block truncate text-[11px] text-neutral-500">
                          {concentration}
                        </span>
                      )}
                    </button>
                  </th>
                  {carrierDrugs.map((c) => {
                    const cell = getCell(state, d.id, c.id)
                    if (!cell) {
                      return (
                        <td
                          key={c.id}
                          aria-label={t('compatibility.carriers.noEntry', 'No entry')}
                          className="h-11 w-14 rounded-[6px] bg-neutral-100 sm:h-8 sm:w-12"
                        />
                      )
                    }
                    const tone = VERDICT_TONE[cell.verdict]
                    const dim = !visibleTones.has(tone)
                    const isPairSelected =
                      selectedPair !== null &&
                      ((selectedPair[0] === d.id && selectedPair[1] === c.id) ||
                        (selectedPair[1] === d.id && selectedPair[0] === c.id))
                    return (
                      <td key={c.id} className="p-0">
                        <button
                          type="button"
                          onClick={() => onSelectCell(d, c, cell)}
                          aria-label={`${d.label} × ${c.label}: ${t(`compatibility.verdicts.${cell.verdict}`)}`}
                          className={cn(
                            'block h-11 w-14 rounded-[6px] transition active:scale-95 sm:h-8 sm:w-12',
                            TONE_FILL[tone],
                            dim && 'opacity-25',
                            isPairSelected &&
                              'outline outline-2 outline-offset-1 outline-primary-focus',
                          )}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
